import React, { useEffect, useState } from "react";
import Table from "../../../designComponents/Table";
import { ReactComponent as View } from "../../../assets/view.svg";
import {
  renderFullDetails,
  renderstatus,
} from "../../../designComponents/Table/renderMethods";
import {
  ButtonsSection,
  Card,
  LabelText,
  SectionTitle,
  ValueText,
} from "../../Properties/Details/styles";
import Pagination from "../../../components/Pagination";
import users, { paginationType } from "../../../apiConfigs/users";
import requestAPI from "../../../utils/requestAPI";
import { useNavigate } from "react-router-dom";
import Modal from "../../../designComponents/Modal";
import { humanReadableDateWithTime } from "../../../utils/date";

interface UserDetails {
  _id: string;
  avatar: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  fullName: string;
}

interface Metadata {
  contractId: string;
  bookingId: string;
  isFirstRent: boolean;
  tenantId: string;
  type: string;
}

interface TransactionData {
  _id: string;
  creditedUser: string;
  debitedUser: string;
  bookingId: string;
  transferredAt: string;
  title: string;
  amount: number;
  transactionType: string;
  hasTransferred: boolean;
  status: string;
  platformFee: number;
  metadata: Metadata;
  createdAt: string;
  updatedAt: string;
  __v: number;
  paymentIndentId: string;
  balanceAvailableOn: string;
  chargeId: string;
  creditedUserDetails: UserDetails;
  debitedUserDetails: UserDetails;
}
function Transactions({ userId }: { userId: string }) {
  const [userTransaction, setTransactions] = useState<TransactionData[]>();
  const [paginationParams, setPaginationParams] = useState<paginationType>();
  const [openModal, setOpenModal] = useState<string | null>(null);
  const navigate = useNavigate();
  const getUserTransactions = async ({
    userId,
    params,
  }: {
    userId: string;
    params: paginationType;
  }) => {
    const response = await requestAPI(
      users.getUsersTransactions({ userId, params })
    );
    setTransactions(response.data);
    setPaginationParams(response.pagination);
  };
  useEffect(() => {
    if (userId) {
      getUserTransactions({ userId: userId, params: paginationParams ?? {} });
    }
  }, [userId]);
  const tableData = {
    headings: [
      { key: "cu", label: "Credited User" },
      { key: "du", label: "Debited User" },
      { key: "title", label: "Cause" },
      { key: "amount", label: "Amount Euro" },
      { key: "status", label: "Status" },
      { key: "type", label: "Status" },
      { key: "actions", label: "Actions" },
    ],
    body: Array.isArray(userTransaction)
      ? userTransaction.map((transaction, index) => ({
          id: transaction?._id,
          cu: renderFullDetails({
            firstName: transaction?.creditedUserDetails?.firstName,
            lastName: transaction?.creditedUserDetails?.lastName,
            avatar: transaction?.creditedUserDetails?.avatar,
            email: transaction?.creditedUserDetails?.email,
          }),
          du: renderFullDetails({
            firstName: transaction?.debitedUserDetails?.firstName,
            lastName: transaction?.debitedUserDetails?.lastName,
            avatar: transaction?.debitedUserDetails?.avatar,
            email: transaction?.debitedUserDetails?.email,
          }),
          title: transaction.title,
          status: renderstatus(transaction.status),
          type: transaction.transactionType,
          amount: `€${transaction.amount}`,
          actions: (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <View fill="#7E7E7E"></View>
            </div>
          ),
        }))
      : [],
  };
  const handleCurrentPage = (page: number) => {
    setPaginationParams((prevParams) => ({ ...prevParams, page }));
  };

  const handleLimitChange = (limit: number) => {
    setPaginationParams((prevParams) => ({ ...prevParams, limit, page: 1 }));
  };
  return (
    <Card style={{ margin: 0 }}>
      <SectionTitle style={{ paddingBottom: 10 }}>
        User Transactions
      </SectionTitle>
      <Table
        evenRowBgColor={"#fff"}
        rowClick={(id) => {
          // navigate(id);
          setOpenModal(id);
        }}
        tableData={tableData}
        showCheckBox={false}
      ></Table>
      <Pagination
        activePage={paginationParams?.currentPage ?? 1}
        totalPages={paginationParams?.totalPages ?? 1}
        limit={paginationParams?.limit ?? 10}
        onCurrentPageChange={handleCurrentPage}
        onLimitChange={handleLimitChange}
      />
      {userTransaction?.map((transaction) => {
        return (
          <Modal
            title="Transaction Detail"
            isOpen={openModal === transaction._id}
            halfWidth={true}
            onClose={() => {
              setOpenModal(null);
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 20,
                overflowY: "scroll",
                scrollbarWidth: "thin",
                padding: 10,
                maxHeight: 400,
              }}
            >
              <ButtonsSection>
                <LabelText>Title</LabelText>
                <ValueText>{transaction.title}</ValueText>
              </ButtonsSection>
              <ButtonsSection>
                <LabelText>Id</LabelText>
                <ValueText>{transaction._id}</ValueText>
              </ButtonsSection>
              <ButtonsSection>
                <LabelText>Transaction Type</LabelText>
                <ValueText>{transaction.transactionType}</ValueText>
              </ButtonsSection>
              <ButtonsSection>
                <LabelText>Transaction Amount</LabelText>
                <ValueText>€{transaction.amount}</ValueText>
              </ButtonsSection>
              <ButtonsSection>
                <LabelText>Fee</LabelText>
                <ValueText>{transaction.platformFee}</ValueText>
              </ButtonsSection>
              <ButtonsSection>
                <LabelText>Transaction Date</LabelText>
                <ValueText>
                  {humanReadableDateWithTime(transaction.createdAt)}
                </ValueText>
              </ButtonsSection>

              <ButtonsSection>
                <LabelText>Transfer Status</LabelText>
                <ValueText>
                  {transaction.hasTransferred === false
                    ? "Not Completed"
                    : "Completed"}
                </ValueText>
              </ButtonsSection>
              {transaction.hasTransferred !== false && (
                <ButtonsSection>
                  <LabelText>Transferred At</LabelText>
                  <ValueText>
                    {humanReadableDateWithTime(transaction.transferredAt)}
                  </ValueText>
                </ButtonsSection>
              )}
              <ButtonsSection>
                <LabelText>Credited User</LabelText>
                {renderFullDetails({
                  firstName: transaction?.creditedUserDetails?.firstName,
                  lastName: transaction?.creditedUserDetails?.lastName,
                  avatar: transaction?.creditedUserDetails?.avatar,
                  email: transaction?.creditedUserDetails?.email,
                })}
              </ButtonsSection>
              <ButtonsSection>
                <LabelText>Debited User</LabelText>
                {renderFullDetails({
                  firstName: transaction?.debitedUserDetails?.firstName,
                  lastName: transaction?.debitedUserDetails?.lastName,
                  avatar: transaction?.debitedUserDetails?.avatar,
                  email: transaction?.debitedUserDetails?.email,
                })}
              </ButtonsSection>
              <ButtonsSection>
                <LabelText>Status</LabelText>
                {renderstatus(transaction.status)}
              </ButtonsSection>
              <ButtonsSection>
                <LabelText>Charge Id</LabelText>
                <ValueText>{transaction.chargeId}</ValueText>
              </ButtonsSection>
              <ButtonsSection>
                <LabelText>Payment Indent Id</LabelText>
                <ValueText>{transaction.paymentIndentId}</ValueText>
              </ButtonsSection>
            </div>
          </Modal>
        );
      })}
    </Card>
  );
}

export default Transactions;
